export type AiPipelineId =
  | "script_breakdown"
  | "character_breakdown"
  | "coverage"
  | "budget"
  | "schedule";

export type AiPipelineAccent = "sky" | "brand" | "indigo" | "emerald" | "violet";

export type AiPipelineVisualVariant =
  | "script_pages"
  | "character_profiles"
  | "coverage_document"
  | "budget_grid"
  | "schedule_strip";

export type AiPipelineCatalogEntry = {
  title: string;
  pausedHint: string;
  steps: string[];
  /** Rotating micro-copy while a step is active (cycles every few seconds). */
  stepActivityHints: string[][];
  durationHint: string;
  longRunHint: string;
  progressEstimateNote: string;
  accent: AiPipelineAccent;
  visual: AiPipelineVisualVariant;
  /**
   * Relative time share per step while backend progress is unavailable.
   * Values are proportional (e.g. [1, 1.2, 1.4, 0.8]); omit for equal 8s steps.
   */
  stepDurationWeights?: number[];
  /**
   * Total client-side duration estimate used for staged fallback progress.
   * The run still completes as soon as the backend status resolves.
   */
  estimatedTotalMs?: number;
  longRunThresholdMs?: number;
};

export const AI_PIPELINE_STEP_INTERVAL_MS = 8_000;

export const AI_PIPELINE_STEP_ACTIVITY_INTERVAL_MS = 4_000;

export const AI_PIPELINE_LONG_RUN_MS = 5 * 60 * 1000;

export const AI_PIPELINE_PRODUCTION_PLANNING_ESTIMATE_MS = 7 * 60 * 1000;

export const aiPipelineCatalog: Record<AiPipelineId, AiPipelineCatalogEntry> = {
  script_breakdown: {
    title: "Running script breakdown",
    pausedHint: "Editing paused until this finishes.",
    steps: [
      "Reading scenes and structure",
      "Identifying tags (cast, props, locations, and more)",
      "Applying script tags",
      "Syncing breakdown into your project",
    ],
    stepActivityHints: [
      ["Scanning scene headings…", "Mapping act structure…", "Reviewing page flow…"],
      [
        "Finding character names…",
        "Cataloging locations and items…",
        "Linking elements to scenes…",
      ],
      ["Applying tag categories…", "Updating script markup…", "Validating tagged elements…"],
      ["Writing results to your project…", "Refreshing the editor…", "Almost ready…"],
    ],
    durationHint: "Usually 1–3 minutes",
    longRunHint:
      "Still working — large scripts can take up to 25 minutes. If it fails, retry from Run AI.",
    progressEstimateNote: "Stages are approximate — timing varies by script length.",
    accent: "sky",
    visual: "script_pages",
    stepDurationWeights: [1, 1.2, 1, 0.9],
  },
  character_breakdown: {
    title: "Running character breakdown",
    pausedHint: "Profiles refresh when this finishes.",
    steps: [
      "Scanning the script for speaking roles",
      "Building character profiles",
      "Drafting appearance and personality notes",
      "Saving breakdown cards",
    ],
    stepActivityHints: [
      ["Listing speaking roles…", "Matching names across scenes…", "Grouping ensemble cast…"],
      ["Drafting profile summaries…", "Pulling dialogue context…", "Organizing cast list…"],
      ["Writing appearance notes…", "Summarizing personality traits…", "Refining descriptions…"],
      ["Saving profile cards…", "Updating character pages…", "Almost ready…"],
    ],
    durationHint: "Usually 1–3 minutes",
    longRunHint:
      "Still working — scripts with many characters can take up to 25 minutes. Retry from Run AI if it fails.",
    progressEstimateNote: "Stages are approximate — timing varies by cast size.",
    accent: "brand",
    visual: "character_profiles",
    stepDurationWeights: [1, 1.15, 1.35, 0.85],
  },
  coverage: {
    title: "Running coverage",
    pausedHint: "Editing paused until this finishes.",
    steps: [
      "Reviewing story structure and tone",
      "Analyzing characters and pacing",
      "Drafting summary and comments",
      "Updating your coverage report",
    ],
    stepActivityHints: [
      ["Reading story beats…", "Assessing tone and genre…", "Reviewing structure…"],
      ["Evaluating character arcs…", "Checking pacing…", "Comparing scene flow…"],
      ["Drafting logline and summary…", "Writing development notes…", "Polishing comments…"],
      ["Updating report sections…", "Formatting coverage…", "Almost ready…"],
    ],
    durationHint: "Usually 2–4 minutes",
    longRunHint:
      "Still working — longer scripts can take up to 25 minutes. Retry from Run AI if it fails.",
    progressEstimateNote: "Stages are approximate — timing varies by script length.",
    accent: "violet",
    visual: "coverage_document",
    stepDurationWeights: [1, 1.2, 1.45, 0.75],
  },
  budget: {
    title: "Running budget",
    pausedHint: "Edits paused until this finishes.",
    steps: [
      "Reading tagged script elements",
      "Estimating line items and categories",
      "Applying contingencies and fringes",
      "Preparing your topsheet",
    ],
    stepActivityHints: [
      ["Reading tagged elements…", "Grouping line items…", "Scanning categories…"],
      ["Calculating estimates…", "Applying rate tables…", "Balancing categories…"],
      ["Applying fringes…", "Adding contingencies…", "Validating totals…"],
      ["Building topsheet…", "Syncing budget grid…", "Almost ready…"],
    ],
    durationHint: "Usually 5–7 minutes",
    longRunHint: "Still working — complex budgets can take a little over 7 minutes.",
    progressEstimateNote: "Stages are approximate — timing varies by budget complexity.",
    accent: "brand",
    visual: "budget_grid",
    estimatedTotalMs: AI_PIPELINE_PRODUCTION_PLANNING_ESTIMATE_MS,
    longRunThresholdMs: AI_PIPELINE_PRODUCTION_PLANNING_ESTIMATE_MS,
  },
  schedule: {
    title: "Running schedule",
    pausedHint: "Edits paused until this finishes.",
    steps: [
      "Reading scenes and locations",
      "Grouping shoot days",
      "Ordering the stripboard",
      "Applying your schedule settings",
    ],
    stepActivityHints: [
      ["Reading scene locations…", "Grouping company moves…", "Scanning shoot days…"],
      ["Building shoot days…", "Balancing page counts…", "Assigning units…"],
      ["Ordering stripboard rows…", "Placing shoot days…", "Optimizing sequence…"],
      ["Applying calendar settings…", "Saving stripboard…", "Almost ready…"],
    ],
    durationHint: "Usually 5–7 minutes",
    longRunHint: "Still working — large schedules can take a little over 7 minutes.",
    progressEstimateNote: "Stages are approximate — timing varies by scene count.",
    accent: "indigo",
    visual: "schedule_strip",
    estimatedTotalMs: AI_PIPELINE_PRODUCTION_PLANNING_ESTIMATE_MS,
    longRunThresholdMs: AI_PIPELINE_PRODUCTION_PLANNING_ESTIMATE_MS,
  },
};

export function getAiPipelineConfig(pipeline: AiPipelineId): AiPipelineCatalogEntry {
  return aiPipelineCatalog[pipeline];
}
